import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, Loader2, CheckCircle2, ArrowRight, Plus, Check, Eye, EyeOff, Minus } from 'lucide-react';
interface DashboardCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  allAvailableDashboards: Array<{
    id: string;
    name: string;
    description: string;
    category: string;
  }>;
  existingDashboardIds: string[];
  onAddDashboard: (dashboardId: string) => void;
  onRemoveDashboard?: (dashboardId: string) => void;
  onCreateDashboard: (prompt: string) => Promise<void>;
}
export function DashboardCreationModal({
  isOpen,
  onClose,
  allAvailableDashboards,
  existingDashboardIds,
  onAddDashboard,
  onRemoveDashboard,
  onCreateDashboard
}: DashboardCreationModalProps) {
  const [activeTab, setActiveTab] = useState<'browse' | 'create'>('browse');
  const [prompt, setPrompt] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showAddedDashboards, setShowAddedDashboards] = useState(true);
  // Group dashboards by category
  const dashboardsByCategory = allAvailableDashboards.reduce((acc, dashboard) => {
    if (!acc[dashboard.category]) {
      acc[dashboard.category] = [];
    }
    acc[dashboard.category].push(dashboard);
    return acc;
  }, {} as Record<string, typeof allAvailableDashboards>);
  // Filter dashboards based on toggle
  const getFilteredDashboards = (dashboards: typeof allAvailableDashboards) => {
    if (showAddedDashboards) {
      return dashboards;
    }
    return dashboards.filter(d => !existingDashboardIds.includes(d.id));
  };
  const examplePrompts = ['Create a pricing dashboard for Germany', 'Nordics weather and wind forecast', 'Spain solar generation analysis', 'UK trading opportunities dashboard', 'France power market overview'];
  const handleCreate = async () => {
    if (!prompt.trim()) return;
    setIsCreating(true);
    try {
      await onCreateDashboard(prompt);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        setPrompt('');
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to create dashboard:', error);
    } finally {
      setIsCreating(false);
    }
  };
  const handleExampleClick = (example: string) => {
    setPrompt(example);
  };
  const handleToggleDashboard = (dashboardId: string) => {
    const isAdded = existingDashboardIds.includes(dashboardId);
    if (isAdded && onRemoveDashboard) {
      onRemoveDashboard(dashboardId);
    } else {
      onAddDashboard(dashboardId);
    }
  };
  return <AnimatePresence data-id="element-216">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50" data-id="element-217" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-218">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full overflow-hidden max-h-[90vh] flex flex-col" data-id="element-219">
              {/* Header */}
              <div className="relative px-6 py-5 border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white" data-id="element-220">
                <div className="flex items-center justify-between" data-id="element-221">
                  <div className="flex items-center gap-3" data-id="element-222">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center" data-id="element-223">
                      <Sparkles size={20} className="text-white" data-id="element-224" />
                    </div>
                    <div data-id="element-225">
                      <h2 className="text-xl font-bold text-slate-900" data-id="element-226">
                        Add Dashboard
                      </h2>
                      <p className="text-sm text-slate-500 mt-0.5" data-id="element-227">
                        Browse available dashboards or create a custom one
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600" data-id="element-228">
                    <X size={20} data-id="element-229" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mt-4" data-id="element-230">
                  <button onClick={() => setActiveTab('browse')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'browse' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-231">
                    Browse Dashboards
                  </button>
                  <button onClick={() => setActiveTab('create')} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${activeTab === 'create' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-232">
                    Create Custom
                  </button>

                  {/* Toggle for Browse tab */}
                  {activeTab === 'browse' && <motion.div initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} className="ml-auto flex items-center gap-2" data-id="element-233">
                      <span className="text-xs text-slate-600 font-medium" data-id="element-234">
                        Show added
                      </span>
                      <button onClick={() => setShowAddedDashboards(!showAddedDashboards)} className={`relative w-11 h-6 rounded-full transition-all duration-300 ${showAddedDashboards ? 'bg-pink-500' : 'bg-slate-300'}`} data-id="element-235">
                        <motion.div layout transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm flex items-center justify-center ${showAddedDashboards ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-236">
                          {showAddedDashboards ? <Eye size={12} className="text-pink-600" data-id="element-237" /> : <EyeOff size={12} className="text-slate-400" data-id="element-238" />}
                        </motion.div>
                      </button>
                    </motion.div>}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto" data-id="element-239">
                {activeTab === 'browse' ? <div className="p-6 space-y-6" data-id="element-240">
                    {Object.entries(dashboardsByCategory).map(([category, dashboards]) => {
                const filteredDashboards = getFilteredDashboards(dashboards);
                // Skip empty categories when hiding added dashboards
                if (filteredDashboards.length === 0) {
                  return null;
                }
                return <div key={category} data-id="element-241">
                            <div className="flex items-center justify-between mb-3" data-id="element-242">
                              <h3 className="text-sm font-semibold text-slate-700" data-id="element-243">
                                {category}
                              </h3>
                              <span className="text-xs text-slate-500" data-id="element-244">
                                {filteredDashboards.length}{' '}
                                {filteredDashboards.length === 1 ? 'dashboard' : 'dashboards'}
                              </span>
                            </div>
                            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-245">
                              <AnimatePresence mode="popLayout" data-id="element-246">
                                {filteredDashboards.map((dashboard, index) => {
                        const isAdded = existingDashboardIds.includes(dashboard.id);
                        return <motion.button key={dashboard.id} layout initial={{
                          opacity: 0,
                          scale: 0.9
                        }} animate={{
                          opacity: 1,
                          scale: 1,
                          transition: {
                            delay: index * 0.05,
                            duration: 0.2
                          }
                        }} exit={{
                          opacity: 0,
                          scale: 0.9,
                          transition: {
                            duration: 0.15
                          }
                        }} onClick={() => handleToggleDashboard(dashboard.id)} className={`text-left p-4 rounded-xl border transition-all ${isAdded ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-300' : 'bg-white border-slate-200 hover:border-pink-500 hover:shadow-md'}`} data-id="element-247">
                                      <div className="flex items-start justify-between gap-3" data-id="element-248">
                                        <div className="flex-1 min-w-0" data-id="element-249">
                                          <h4 className="font-semibold text-slate-900 mb-1 text-sm" data-id="element-250">
                                            {dashboard.name}
                                          </h4>
                                          <p className="text-xs text-slate-600 line-clamp-2" data-id="element-251">
                                            {dashboard.description}
                                          </p>
                                        </div>
                                        <motion.div initial={false} animate={{
                              scale: 1,
                              rotate: isAdded ? 0 : 0
                            }} transition={{
                              duration: 0.2
                            }} className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${isAdded ? 'bg-emerald-500' : 'bg-slate-100'}`} data-id="element-252">
                                          {isAdded ? <Check size={14} className="text-white" data-id="element-253" /> : <Plus size={14} className="text-slate-400" data-id="element-254" />}
                                        </motion.div>
                                      </div>
                                    </motion.button>;
                      })}
                              </AnimatePresence>
                            </motion.div>
                          </div>;
              })}
                  </div> : <div className="p-6 space-y-6" data-id="element-255">
                    {/* Input Area */}
                    <div data-id="element-256">
                      <label className="block text-sm font-semibold text-slate-700 mb-2" data-id="element-257">
                        What would you like to create?
                      </label>
                      <div className="relative" data-id="element-258">
                        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleCreate();
                    }
                  }} placeholder="e.g., Create a pricing dashboard for Germany with hourly trends and trading opportunities..." className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 resize-none text-slate-900 placeholder:text-slate-400" rows={4} disabled={isCreating || isSuccess} data-id="element-259" />
                        <div className="absolute bottom-3 right-3 flex items-center gap-2" data-id="element-260">
                          <span className="text-xs text-slate-400" data-id="element-261">
                            ⌘ + Enter
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Example Prompts */}
                    <div data-id="element-262">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-263">
                        Example Prompts
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-264">
                        {examplePrompts.map((example, idx) => <button key={idx} onClick={() => handleExampleClick(example)} disabled={isCreating || isSuccess} className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-265">
                            {example}
                          </button>)}
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4" data-id="element-266">
                      <div className="flex gap-3" data-id="element-267">
                        <Sparkles size={16} className="text-blue-600 mt-0.5 flex-shrink-0" data-id="element-268" />
                        <div data-id="element-269">
                          <p className="text-sm font-medium text-blue-900 mb-1" data-id="element-270">
                            AI-Powered Dashboard Creation
                          </p>
                          <p className="text-xs text-blue-700 leading-relaxed" data-id="element-271">
                            Describe your analysis needs in natural language.
                            Our AI will create a customized dashboard with
                            relevant data visualizations, metrics, and insights
                            for your specific region and use case.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>

              {/* Footer - only show for create tab */}
              {activeTab === 'create' && <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-272">
                  <button onClick={onClose} disabled={isCreating || isSuccess} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-273">
                    Cancel
                  </button>
                  <button onClick={handleCreate} disabled={!prompt.trim() || isCreating || isSuccess} className="flex items-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-slate-900" data-id="element-274">
                    {isSuccess ? <>
                        <CheckCircle2 size={18} data-id="element-275" />
                        Created!
                      </> : isCreating ? <>
                        <Loader2 size={18} className="animate-spin" data-id="element-276" />
                        Creating...
                      </> : <>
                        Create Dashboard
                        <ArrowRight size={18} data-id="element-277" />
                      </>}
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}